import { stringToDate, addZero, getWeekdayName, getMonthName, getDateParts } from '../utils/date.js';
import { isString, capitalize } from '../utils/string.js';
import { isNotNull } from '../utils/core.js';
import '../node_modules/tslib/tslib.es6.js';
import LocaleWrapper from './locale-wrapper.js';

class SuperDate {
  /** Input date example: 2024-03-01 00:00:00 */
  constructor(date) {
    this.server = null;
    this.client = null;
    this.server = date;
    // eslint-disable-next-line no-nested-ternary
    this.client = isString(this.server) ? stringToDate(this.server) : date instanceof Date ? date : null;
  }
  isValid() {
    return isNotNull(this.client) && !isNaN(this.client.getTime());
  }
  /** Output example: 1 */
  get day() {
    return this.isValid() ? this.client.getDate() : null;
  }
  /** Output example: '01' */
  get dayTwoSymbols() {
    return this.isValid() ? addZero(this.day) : '00';
  }
  /** Output example: 1 */
  get weekDay() {
    return this.isValid() ? this.client.getDay() : null;
  }
  /** Output example: 'пятница' */
  get longWeekDayName() {
    return this.isValid() ? getWeekdayName(this.client, 'long', LocaleWrapper.locale) : '';
  }
  /** Output example: 'пт' */
  get shortWeekDayName() {
    return this.isValid() ? getWeekdayName(this.client, 'short', LocaleWrapper.locale) : '';
  }
  /** Output example: 3 for March */
  get month() {
    return this.isValid() ? this.client.getMonth() + 1 : null;
  }
  /** Output example: '03' */
  get monthTwoSymbols() {
    return this.isValid() ? addZero(this.month) : '00';
  }
  /** Output example: 'март' */
  get longMonthName() {
    return this.isValid() ? getMonthName(this.client, 'long', LocaleWrapper.locale) : '';
  }
  /** Output example: 'мар' */
  get shortMonthName() {
    return this.isValid() ? getMonthName(this.client, 'short', LocaleWrapper.locale).toLowerCase().substring(0, 3) : '';
  }
  /** Output example: 'марта' */
  get declensionMonthName() {
    return this.isValid() ? getDateParts(this.client, {
      day: 'numeric',
      month: 'long'
    }, LocaleWrapper.locale).month : '';
  }
  /** Output example: 2024 */
  get year() {
    return this.isValid() ? this.client.getFullYear() : NaN;
  }
  /** Output example: '24' */
  get yearTwoSymbols() {
    return this.isValid() ? `${this.year}`.substring(2, 4) : '';
  }
  /** Returns True if the current year matches the specified one. */
  get isCurrentYear() {
    const currentYear = new Date().getFullYear();
    return this.year === currentYear;
  }
  /** Output example: '01.03.24' */
  get date() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols}.${this.yearTwoSymbols}` : '...';
  }
  /** Output example: '01.03.24 00:00' */
  get dateWithTime() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols}.${this.yearTwoSymbols} ${this.time}` : '...';
  }
  /** Output example: '01.03' */
  get dateWithoutCurrentYear() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols}${this.isCurrentYear ? '' : `.${this.yearTwoSymbols}`}` : '...';
  }
  /** Output example: '01.03.2024' */
  get dateWithFullYear() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols}.${this.year}` : '...';
  }
  /** Output example: '1 марта 2024' */
  get prettyDate() {
    return this.isValid() ? `${this.day} ${this.declensionMonthName} ${this.year}` : '...';
  }
  /** Output example: '01.03' */
  get shortDate() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols}` : '...';
  }
  /** Output example: '01МАР' */
  get shortDateUpper() {
    return this.isValid() ? `${this.dayTwoSymbols}${this.shortMonthName.toUpperCase()}` : '...';
  }
  /** Output example: '01МАР24' */
  get shortDateUpperWithYear() {
    return this.isValid() ? `${this.dayTwoSymbols}${this.shortMonthName.toUpperCase()}${this.yearTwoSymbols}` : '...';
  }
  /** Output example: '01.03 00:00' */
  get shortDateWithTime() {
    return this.isValid() ? `${this.dayTwoSymbols}.${this.monthTwoSymbols} ${this.time}` : '...';
  }
  /** Output example: '1 мар 00:00' */
  get prettyDateWithTime() {
    return this.isValid() ? `${this.day} ${this.shortMonthName} ${this.time}` : '...';
  }
  /** Output example: '1 мар 24' */
  get shortPrettyDate() {
    return this.isValid() ? `${this.day} ${this.shortMonthName} ${this.yearTwoSymbols}` : '...';
  }
  /** Output example: '1 мар, пт' */
  get prettyDateWithWeekDay() {
    return this.isValid() ? `${this.day} ${this.shortMonthName}, ${this.shortWeekDayName}` : '...';
  }
  /** Output example: '1 мар' */
  get prettyDateWithoutCurrentYear() {
    return this.isValid() ? `${this.day} ${this.shortMonthName}${this.isCurrentYear ? '' : ` ${this.yearTwoSymbols}`}` : '...';
  }
  /** Output example: 0 */
  get hours() {
    return this.isValid() ? this.client.getHours() : NaN;
  }
  /** Output example: '00' */
  get hoursTwoSymbols() {
    return this.isValid() ? addZero(this.hours) : '00';
  }
  /** Output example: 0 */
  get minutes() {
    return this.isValid() ? this.client.getMinutes() : null;
  }
  /** Output example: '00' */
  get minutesTwoSymbols() {
    return this.isValid() ? addZero(this.minutes) : '00';
  }
  /** Output example: '00:00' */
  get time() {
    return `${this.hoursTwoSymbols}:${this.minutesTwoSymbols}`;
  }
  /** Returns the date uid in the "${DD}${MM}${HH}${mm}" format */
  get uid() {
    return `${this.day}${this.month}${this.hours}${this.minutes}`;
  }
  /** output: { date: { date: '01', month: { value: '03', label: 'Март' }, year: 2024, isValid: true } } */
  toInput(withTime = false) {
    return this.isValid() ? {
      date: Object.assign({
        date: this.dayTwoSymbols,
        month: {
          value: this.monthTwoSymbols,
          label: capitalize(this.longMonthName)
        },
        year: this.year,
        isValid: true
      }, withTime ? {
        time: {
          full: `${this.hoursTwoSymbols}:${this.minutesTwoSymbols}:00`,
          hours: `${this.hoursTwoSymbols}`,
          minutes: `${this.minutesTwoSymbols}`
        }
      } : {})
    } : {};
  }
  /** Output example: '2024-03-01 12:00:00' */
  toServer(withTime = true) {
    return this.isValid() ? `${this.year}-${this.monthTwoSymbols}-${this.dayTwoSymbols}${withTime ? ` ${this.time}:00` : ''}` : '';
  }
  toJSON() {
    return Object.assign({}, this);
  }
}

export { SuperDate as default };
